import { Alert, Divider, TextField } from '@mui/material';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { orderLicence } from '../../api/admin/endpoints';
import type { LicenceOrderInput, TenantDetail } from '../../api/admin/types';
import { useAppMutation } from '../../api/useAppMutation';
import { FormDialog } from '../../components/FormDialog';
import { LicenceFields } from '../../components/LicenceFields';
import { licenceDraftFrom } from '../../components/licenceDraft';
import { licenceOrderInputFrom, type LicenceOrderDraft } from '../../components/licenceOrderDraft';
import { useT } from '../../i18n/LanguageContext';

import { QuotePreview } from './QuotePreview';

/** A renewal — or a new first order after a cancelled one. Issues a proforma; nothing changes until it is paid. */
export const OrderLicenceDialog = ({ tenant, onClose }: { tenant: TenantDetail; onClose: () => void }) => {
  const t = useT();
  const navigate = useNavigate();
  const [draft, setDraft] = useState<LicenceOrderDraft>({
    ...licenceDraftFrom(tenant.licence),
    months: '12',
  });
  const [problem, setProblem] = useState<string | null>(null);
  const order = licenceOrderInputFrom(draft);
  const mutation = useAppMutation(
    (input: LicenceOrderInput) => orderLicence(tenant.id, input),
    ['tenant', 'tenants', 'invoices', 'audit'],
    (proforma) => navigate(`/platform/invoices/${proforma.id}`),
  );

  const submit = (): void => {
    if (order === null) {
      setProblem(t('licence.orderInvalid'));

      return;
    }

    setProblem(null);
    mutation.mutate(order);
  };

  return (
    <FormDialog
      title={t('licence.orderTitle', { tenant: tenant.name })}
      onClose={onClose}
      onSubmit={submit}
      submitLabel={t('licence.order')}
      isBusy={mutation.isPending}
      error={mutation.error}
      problem={problem}
      maxWidth="md"
    >
      <Alert severity="info" variant="outlined">
        {t('licence.orderHint')}
      </Alert>
      <TextField
        required
        label={t('field.months')}
        value={draft.months}
        inputProps={{ inputMode: 'numeric' }}
        onChange={(event) => setDraft({ ...draft, months: event.target.value })}
        sx={{ maxWidth: 200 }}
      />
      <LicenceFields
        draft={draft}
        onChange={(next) => setDraft({ ...draft, ...next })}
        showNotes
        showDates={false}
      />
      <Divider />
      <QuotePreview order={order} />
    </FormDialog>
  );
};
